import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  IconButton,
  Chip,
  Collapse,
  Button,
  TextField,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { axiosService } from 'shared/helpers/axios';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Group as GroupIcon,
  Reply as ReplyIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  VisibilityOff as VisibilityOffIcon,
  Repeat as RepeatIcon,
  Groups as OtableWideIcon,
  AutoStories as ReflectionIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import EntityLink from './links/EntityLink';
import EmojiReactionButton from 'components/common/buttons/EmojiReactionButton';
import { formatDistanceToNow } from 'date-fns';
import { SuggestButton, InviteButton, MeetButton, MessageButton } from 'components/social/profile/ActionButtons';
import MeetingScheduleDialog from '../common/dialogs/MeetingScheduleDialog';
import InviteToConsortiumDialog from "../common/dialogs/InviteToConsortiumDialog";
import SuggestEntityDialog from "../common/dialogs/SuggestEntityDialog";
import { Comments } from './Comments';
import LinkPreviewCard from './LinkPreviewCard';
import YouTubeEmbed from './YouTubeEmbed';
import RepostedOriginal from './RepostedOriginal';
import { PostShareButton, RepostButton, recordPostEvent } from './PostShare';
import EditableLinkPreview from './EditableLinkPreview';
import ReportButton from './ReportButton';
import useLinkPreview from 'shared/hooks/useLinkPreview';
import { mentionsIn, plainMentions, useMentions } from 'components/social/feed/mentions';
import PostStatsButton from './PostStats';
import WhyForMe, { WhyForMeButton } from './WhyForMe';
import FollowButton from './buttons/FollowButton';
import { findYouTube, parseYouTube } from 'shared/helpers/youtube';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { reflectionSavedMessage, savePostAsReflection } from 'shared/helpers/reflectionFromPost';
import LabMarkdown, { SafeImage } from 'components/collab/lab/LabMarkdown';
import { POST_MAX_LENGTH, hashtagsOf, lengthHint, linkedImageOf, postMarkdown, withLink } from 'shared/helpers/postText';

// Who sees a post, widest first (the server's Post.VISIBILITY_CHOICES)
// A post reads like a short letter: compact lines and paragraphs, a little larger than the UI text. Scoped to
// posts (labs and reflections share the markdown renderer and keep their roomier long-form spacing).
const POST_LINE = 1.45;
const POST_FONT = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const POST_TEXT_SX = {
  fontFamily: POST_FONT,
  fontSize: '13.5px',
  color: 'text.primary',
  '& p, & li': { fontFamily: 'inherit', fontSize: 'inherit', lineHeight: POST_LINE, letterSpacing: '-0.006em' },
  '& p': { mb: '0.5em' },
  '& ul, & ol': { my: '0.35em', pl: 2.5 },
  '& li': { mb: '0.15em' },
  '& blockquote': { my: '0.5em' },
};

const VISIBILITY_LABELS = { public: 'Public', otable: 'Everyone', connections: 'My connections', private: 'Only me' };
const VISIBILITY_HINTS = {
  public: 'Anyone on the web, with the link — no account needed',
  otable: 'Everyone signed in to Otable',
  connections: 'Your mutual follows and contacts',
  private: 'Only you',
};

/**
 * PostItem component - displays a single post in the activity feed
 * Similar structure to CommentItem
 */
function PostItem({ 
  post, 
  currentUser, 
  compact = false,
  showNetworkLabel = false,
  showFollow = false,          // the author is someone the viewer does not follow: a Follow button under the name
  onRemove 
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isAuthor = String(post.author.id) === String(currentUser?.id);
  
// console.log("***** POST", post)

  const showActions = currentUser && !isAuthor; // Only show action buttons if user is logged in and not the author
  const [expanded, setExpanded] = useState(false);
  const [inviteToConsortiumDialogOpen, setInviteToConsortiumDialogOpen] = useState(false);
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ title: '', body: '', visibility: 'otable' });
  const [saving, setSaving] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [localCommentsCount, setLocalCommentsCount] = useState(post.comments_count || 0);
  // Who sees the post: the author changes it at any time from the chip's menu
  const [visibility, setVisibility] = useState(post.visibility || 'otable');
  const [visibilityMenu, setVisibilityMenu] = useState(null);
  const { showSnackbar = () => {} } = useSnackbar() || {};

  const changeVisibility = async (value) => {
    setVisibilityMenu(null);
    if (value === visibility) return;
    const before = visibility;
    setVisibility(value);
    try {
      const response = await axiosService.patch(`/interact/posts/${post.id}/`, { visibility: value });
      post.visibility = response.data.visibility;
      window.dispatchEvent(new CustomEvent('postUpdated', { detail: { postId: post.id, post: response.data } }));
      showSnackbar(`Visible to: ${VISIBILITY_LABELS[value].toLowerCase()}`, 'success');
    } catch (error) {
      setVisibility(before);
      showSnackbar(error.response?.data?.detail || 'Could not change who sees the post.', 'error');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    setDeleting(true);
    try {
      await axiosService.delete(`/interact/posts/${post.id}/`);
      // Remove the post from DOM by hiding it
      const postElement = document.getElementById(`post-${post.id}`);
      if (postElement) {
        postElement.style.display = 'none';
      }
      // Dispatch event to notify parent components to refresh
      window.dispatchEvent(new CustomEvent('postDeleted', { detail: { postId: post.id } }));
    } catch (error) {
      // console.error('Failed to delete post:', error);
      alert('Failed to delete post. Please try again.');
      setDeleting(false);
    }
  };

  // Editing shows the post as posted: plain "@Name" mentions (links again when saved) and its link card
  const setEditBody = (next) => setEditData((d) => ({ ...d, body: typeof next === 'function' ? next(d.body) : next }));
  const editCard = useLinkPreview(isEditing ? editData.body : '', setEditBody);
  const editInputRef = useRef(null);
  const editMentions = useMentions(editData.body || '', setEditBody, editInputRef);
  const handleEditStart = () => {
    const body = plainMentions(post.body || '');
    setEditData({
      title: post.title || '',
      body,
      visibility
    });
    editMentions.reset(mentionsIn(post.body || ''));     // the post's mentions stay links when saved
    editCard.reset(post.link_preview || null, body);
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditData({ title: '', body: '', visibility: 'otable' });
  };

  const handleEditSave = async () => {
    if (!editData.body.trim()) return;

    setSaving(true);
    try {
      const response = await axiosService.patch(`/interact/posts/${post.id}/`, {
        title: editData.title,
        body: editMentions.apply(editData.body),
        visibility: editData.visibility,
        link_preview: editCard.linkPreview || null,
      });
      
      // Update the post data in place
      post.title = response.data.title;
      post.body = response.data.body;
      post.visibility = response.data.visibility;
      post.link_preview = response.data.link_preview;
      setVisibility(response.data.visibility);
      
      // Dispatch event to notify parent components to refresh
      window.dispatchEvent(new CustomEvent('postUpdated', { detail: { postId: post.id, post: response.data } }));
      
      setIsEditing(false);
    } catch (error) {
      // console.error('Failed to update post:', error);
      alert(error.response?.data?.detail || 'Failed to update post. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleMessage = () => {
    recordPostEvent(post, 'message');
    navigate(`/messages/new/?user=${post.author_info?.id || post.author?.id   }`);
  };
  // the author's choice of action buttons for this viewer (all of them when the server did not say)
  const allowed = (key) => !Array.isArray(post.actions) || post.actions.includes(key);
  const author = post.author_info || post.author;          // the person the actions are about
  
  const handleToggleComments = () => {
    setShowComments(!showComments);
  };

  const handleCommentCountChange = (count) => {
    setLocalCommentsCount(count);
    // Update post object as well
    post.comments_count = count;
  };

  // Visibility options cycle order (matches backend Post model choices)
  const visibilityOptions = ['otable', 'public', 'connections', 'private'];

  const cycleVisibility = () => {
    const currentIndex = visibilityOptions.indexOf(editData.visibility);
    const nextIndex = (currentIndex + 1) % visibilityOptions.length;
    setEditData({ ...editData, visibility: visibilityOptions[nextIndex] });
  };

  // Get visibility icon for a given value
  const getVisibilityIcon = (value) => {
    const v = value || visibility;
    switch (v) {
      case 'public':
        return <PublicIcon sx={{ fontSize: 12 }} />;
      case 'otable':
        return <OtableWideIcon sx={{ fontSize: 12 }} />;
      case 'connections':
        return <GroupIcon sx={{ fontSize: 12 }} />;
      case 'private':
        return <LockIcon sx={{ fontSize: 12 }} />;
      default:
        return null;
    }
  };

  const shouldTruncate = compact && post.body && post.body.length > 200;
  const isPlainRepost = post.is_repost && !(post.body || '').trim();
  // "Why is this for me?": the AI's answer (kept from before, or asked now)
  const whyForMe = useState(post.why_for_me?.answer || null);
  // the author's hashtags: their post can become a reflection with those tags
  const tags = isAuthor ? hashtagsOf(post.body || '') : [];
  // saved straight away, titled "Post on <the post's date>"; the button then opens the reflection
  const [reflection, setReflection] = useState(post.reflection || null);   // {id, status} once saved
  useEffect(() => {                                 // saved from the composer: arrives with the refreshed post
    if (post.reflection) setReflection(post.reflection);
  }, [post.reflection?.id, post.reflection?.status]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [savingReflection, setSavingReflection] = useState(false);
  const addAsReflection = async () => {
    setSavingReflection(true);
    try {
      const saved = await savePostAsReflection({ body: withLink(post.body || '', post.link_preview?.url), tags, date: post.created, postId: post.id });
      setReflection(saved);
      showSnackbar(reflectionSavedMessage(saved), 'success');
    } catch (err) {
      showSnackbar('The reflection could not be saved.', 'error');
    } finally {
      setSavingReflection(false);
    }
  };
  // "Ayşe Demir reposted John Carter's post with a comment" / "You reposted a post that was deleted"
  const repostLine = (() => {
    if (!post.is_repost) return '';
    const who = isAuthor ? 'You' : (post.author_name || 'Someone');
    const orig = post.original || {};
    const origAuthor = orig.author && [orig.author.first_name, orig.author.last_name].filter(Boolean).join(' ');
    const origIsViewer = orig.author && String(orig.author.id) === String(currentUser?.id);
    const noun = orig.kind === 'interest' ? 'interest' : 'post';
    const whose = orig.unavailable
      ? (orig.reason === 'deleted' ? 'a post that was deleted' : 'a post you cannot see')
      : origIsViewer ? (isAuthor ? `your own ${noun}` : `your ${noun}`) : `${origAuthor || orig.author_name || 'someone'}'s ${noun}`;
    return `${who} reposted ${whose}${isPlainRepost ? '' : ' with a comment'}`;
  })();
  // a YouTube link (the preview's or one in the text) plays in the post; another link keeps its preview card
  const video = findYouTube(post.link_preview?.url, post.body);
  const previewIsVideo = Boolean(parseYouTube(post.link_preview?.url));
  const linkedImage = linkedImageOf(post.link_preview);      // a link to an image: the image, at the bottom

  return (
    <Box 
      id={`post-${post.id}`}
      sx={{ 
        display: 'flex', 
        gap: { xs: 1, sm: 1.5 }, 
        borderRadius: 1,
        // phones: less padding; a post on its own page fills the page's width
        p: compact ? { xs: 1.25, sm: 2 } : { xs: 1.5, sm: 3 },
        mb: { xs: 2, sm: 5 },
        minWidth: 0,
        scrollMarginTop: '80px',
        background: '#fff',
        // on the feed's white: a faint edge and a soft shadow set each post apart
        border: '1px solid rgba(16, 24, 40, 0.06)',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08), 0 4px 14px rgba(16, 24, 40, 0.06)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 2px 6px rgba(16, 24, 40, 0.10), 0 8px 24px rgba(16, 24, 40, 0.08)',
        }
        
      }}
    >
     
      
      <Box sx={{ flex: 1, pl: { xs: 0, sm: 1 }, minWidth: 0 }}>
        {post.is_repost && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <RepeatIcon sx={{ fontSize: 14 }} />
            {repostLine}
          </Typography>
        )}
        {/* Header with author, timestamp, and actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>


        <EntityLink 
        type="user" 
        id={post.author_info?.id || post.author?.id}
        showAvatar={true}
        avatar={{
          src: post.author_avatar,
          alt: post.author_info?.first_name || post.author_name,
          children: (post.author_info?.first_name || post.author_name)?.[0],
          sx: {
            width: compact ? 36 : 40, 
            height: compact ? 36 : 40, 
            mr: 1, 
            fontSize: compact ? '1.2rem' : '1.5rem',
            backgroundColor: theme.palette.primary.avatar2,
            color: 'white',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
            // mb: 0.5,
            mt: 0.5,
            
          }
        }}
      />

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <EntityLink
                type="user"
                id={post.author_info?.id || post.author?.id}
                name={post.author_info ? 
                  `${post.author_info.first_name} ${post.author_info.last_name}` : 
                  post.author_name || 'Anonymous User'
                }
                variant="subtitle2"
                underline="hover"
              />
              <Typography variant="caption" color="text.secondary">
                {post.created ? formatDistanceToNow(new Date(post.created), { addSuffix: true }) : ''}
              </Typography>
            </Box>
          </Box>

          {/* Network label (followed/consortium) */}
          {/* {showNetworkLabel && post.network_source && (
            <Chip
              size="small"
              label={post.network_source === 'followed_user' ? 'followed' : 'consortium'}
              color={post.network_source === 'followed_user' ? 'info' : 'primary'}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )} */}

   

          {/* Edit and Delete buttons for author */}
          {isAuthor && !isEditing && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
              {!isPlainRepost && <IconButton 
                size="small" 
                onClick={handleEditStart}
                disabled={deleting}
                aria-label="Edit post"
                title="Edit post"
              >
                <EditIcon fontSize="small" />
              </IconButton>}
              <IconButton 
                size="small" 
                onClick={handleDelete}
                disabled={deleting}
                aria-label="Delete post"
                title="Delete post"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              {onRemove && (
                <IconButton 
                  size="small" 
                  onClick={onRemove}
                  aria-label="Remove from feed"
                  title="Remove from feed"
                >
                  <VisibilityOffIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}

          {/* Save/Cancel buttons while editing */}
          {isAuthor && isEditing && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
              <IconButton 
                size="small" 
                onClick={handleEditSave}
                disabled={saving || !editData.body.trim()}
                aria-label="Save changes"
                title="Save changes"
                color="primary"
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={handleEditCancel}
                disabled={saving}
                aria-label="Cancel editing"
                title="Cancel editing"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}


          {showActions && !isAuthor && (
            

<Box
  sx={{
    ml: { xs: 0, md: 'auto' },  // No margin on small screens, 'auto' for alignment on larger screens
    display: {
      xs: 'block',  // Stack vertically for small screens
      md: 'flex'    // Display horizontally for medium and larger screens
    },
    alignItems: 'flex-start',    // Ensure vertical alignment on small screens
    gap: { xs: 1, md: 0 },       // Add slight gap between buttons only on small screens
    textAlign: { xs: 'left', sm: 'left', md: 'right' }, // Ensure proper alignment for small and large screens
  }}
>



              {/* someone you don't follow yet: follow them first, then the other actions */}
              {showFollow && (post.author_info?.id || post.author?.id) && (
                <FollowButton contentType="user" objectId={post.author_info?.id || post.author?.id}
                  sx={{ height: 26, alignSelf: 'center', mr: 0.5, px: 1, fontSize: '0.75rem', lineHeight: 1, textTransform: 'none' }} />
              )}
              {allowed('suggest') && <SuggestButton 
                onClick={() => setSuggestDialogOpen(true)} 
                invertColors={true} 
                fontSize="1.3rem" 
              />}
              {allowed('invite') && <InviteButton 
                onClick={() => setInviteToConsortiumDialogOpen(true)} 
                invertColors={true} 
                fontSize="1.3rem" 
              />}
              {allowed('meet') && <MeetButton 
                onClick={() => setMeetingDialogOpen(true)} 
                invertColors={true} 
                fontSize="1.3rem" 
              />}
              {allowed('message') && <MessageButton 
                onClick={handleMessage} 
                invertColors={true} 
                fontSize="1.3rem" 
              />}
              {onRemove && (
                <IconButton 
                  size="small" 
                  onClick={onRemove}
                  title="Remove from feed"
                  sx={{ ml: 0.5 }}
                >
                  <VisibilityOffIcon sx={{ fontSize: '1.3rem' }} />
                </IconButton>
              )}
            </Box>
          )}

        </Box>

        {/* Post Title */}
        {/* {isEditing ? (
          <TextField
            fullWidth
            variant="standard"
            placeholder="Title (optional)"
            value={editData.title}
            onChange={(e) => setEditData({ ...editData, title: e.target.value })}
            sx={{ mt: 1, '& .MuiInputBase-input': { fontWeight: 600, fontSize: '1rem' } }}
          />
        ) : (
          post.title && (
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mt: 1, 
                fontWeight: 600,
              }}
            >
              {post.title}
            </Typography>
          )
        )} */}

        {/* Post Body */}

        {/* removed by moderators after a report: only its author sees it now, with the reason */}
        {post.removed_at && (
          <Alert severity="warning" sx={{ mt: 1, mr: { xs: 0, sm: 2 } }}>
            Moderators removed this post after a report: only you can see it.
            {post.removal_reason ? ` Reason: ${post.removal_reason}` : ''}
          </Alert>
        )}
        <Box sx={{ minHeight: isPlainRepost && !isEditing ? 0 : 20 }}>
        {isPlainRepost && !isEditing ? null : isEditing ? (



          <TextField
            fullWidth
            multiline
            minRows={1}
            variant="standard"
            placeholder="What's on your mind?"
            value={editData.body}
            inputRef={editInputRef}
            {...editMentions.field}
            inputProps={{ maxLength: POST_MAX_LENGTH }}
            InputProps={{ disableUnderline: true }}
            helperText={lengthHint(editData.body)}
            // the text as the post shows it: same size and spacing, no form field around it
            sx={{ mt: 1, mr: { xs: 0, sm: 2 }, '& .MuiInputBase-root': { fontFamily: POST_FONT, fontSize: POST_TEXT_SX.fontSize, lineHeight: POST_LINE, p: 0 } }}
            autoFocus
          />
        ) : (
          // markdown: links with their own text, bold, lists… (rendered safely: no HTML, no remote images);
          // your own post is edited with its edit button, not by clicking the text
          <Box
            sx={{
              mt: 1,
              mr: { xs: 0, sm: 2 },
              wordBreak: 'break-word',
              ...POST_TEXT_SX,
              // three lines when folded
              ...(shouldTruncate && !expanded && { maxHeight: `${3 * POST_LINE}em`, overflow: 'hidden' }),
            }}
          >
            <LabMarkdown images hashtags>{postMarkdown(post.body)}</LabMarkdown>
          </Box>
        )}

</Box>
        {!isEditing && whyForMe[0] && <WhyForMe answer={whyForMe[0]} onClose={() => whyForMe[1](null)} />}

        {/* Hashtags in your own post: write it up as a reflection with those tags */}
        {!isEditing && tags.length > 0 && (
          reflection ? (
            <Button size="small" variant="text" startIcon={<ReflectionIcon />} onClick={() => navigate(`/reflections/${reflection.id}/`)}
              sx={{ mt: 1, textTransform: 'none' }}>
              {reflection.status === 'published' ? 'Open the reflection' : 'Open the draft reflection'}
            </Button>
          ) : (
            <Button size="small" variant="outlined" startIcon={<ReflectionIcon />} onClick={addAsReflection} disabled={savingReflection}
              sx={{ mt: 1, textTransform: 'none', borderRadius: 4 }}>
              Add as a reflection · {tags.slice(0, 5).map((t) => `#${t}`).join(' ')}
            </Button>
          )
        )}

        {isEditing && editMentions.list}

        {/* Editing: the post's link card, as posted, with a way to remove it (or paste another address) */}
        {isEditing && (
          <EditableLinkPreview linkPreview={editCard.linkPreview} previewLoading={editCard.previewLoading}
            onDismiss={editCard.dismissCard} />
        )}

        {/* A repost: the post it shares */}
        {post.is_repost && !isEditing && <RepostedOriginal original={post.original} />}

        {/* Link Preview: a YouTube link plays in the post */}
        {!isEditing && video && <YouTubeEmbed video={video} title={previewIsVideo ? post.link_preview?.title : undefined} />}
        {!isEditing && post.link_preview?.url && !previewIsVideo && !linkedImage && (
          <LinkPreviewCard preview={post.link_preview} />
        )}
        {!isEditing && linkedImage && <SafeImage src={linkedImage} alt="" />}

        {/* Expand/Collapse button */}
      
        {!isEditing && shouldTruncate && (
            <Box>
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mt: 0.5 }}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Button>
</Box>
        )}
      
        {/* Actions row */}
      
          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 0.25,
            // narrow screens: the buttons wrap to a second line instead of running over each other
            '& .MuiButton-root': { minWidth: 0, px: { xs: 0.5, sm: 1 } } }}>
           <EmojiReactionButton 
              contentType="post" 
              objectId={post.id} 
              currentUser={currentUser} 
              size="small"
            />

{/* Reply/Comments button */}
        <Button
          size="small"
          startIcon={<ReplyIcon />}
          onClick={handleToggleComments}
          sx={{ 
            // mt: 1, 
            textTransform: 'none',
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' }
          }}
        >
          {showComments ? 'Hide' : 'Reply'}
          {localCommentsCount > 0 && ` (${localCommentsCount})`}
        </Button>

        {currentUser && <RepostButton post={post} />}
        {!isEditing && <PostShareButton post={post} withLabel />}
        {!isEditing && post.why_for_me?.eligible && <WhyForMeButton post={post} state={whyForMe} />}
        {!isEditing && isAuthor && <PostStatsButton post={post} />}

       {isEditing ? (
             <Chip
               size="small"
               icon={getVisibilityIcon(editData.visibility)}
               label={VISIBILITY_LABELS[editData.visibility]}
               onClick={cycleVisibility}
               sx={{ ml: 'auto', backgroundColor: 'primary.main', color: 'white', '& .MuiChip-icon': { color: 'white' },
                 '&&:hover': { backgroundColor: 'primary.main' },
                 height: 20, fontSize: '0.7rem' }}
             />
           ) : isAuthor ? (
             <>
               <Tooltip title={`${VISIBILITY_HINTS[visibility]} · click to change`}>
                 <Chip
                   size="small"
                   icon={getVisibilityIcon()}
                   label={VISIBILITY_LABELS[visibility]}
                   variant="outlined"
                   onClick={(e) => setVisibilityMenu(e.currentTarget)}
                   aria-label={`Who sees this post: ${VISIBILITY_LABELS[visibility]}. Change`}
                   // no hover shade: the chip stays as it is, the menu opens on click
                   sx={{ ml: 'auto', height: 20, fontSize: '0.7rem', color: 'text.secondary', borderColor: 'divider',
                     '&&:hover, &&:focus-visible': { bgcolor: 'transparent' } }}
                 />
               </Tooltip>
               <Menu anchorEl={visibilityMenu} open={Boolean(visibilityMenu)} onClose={() => setVisibilityMenu(null)}
                 anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                 {Object.keys(VISIBILITY_LABELS).map((v) => (
                   <MenuItem key={v} selected={v === visibility} onClick={() => changeVisibility(v)}>
                     <ListItemIcon>{getVisibilityIcon(v)}</ListItemIcon>
                     <ListItemText primary={VISIBILITY_LABELS[v]} secondary={VISIBILITY_HINTS[v]} />
                   </MenuItem>
                 ))}
               </Menu>
             </>
           ) : (
             <>
               <Tooltip title={VISIBILITY_LABELS[visibility]}>
                 <Box component="span" sx={{ ml: 'auto', display: 'inline-flex', color: 'text.secondary' }}>
                   {getVisibilityIcon()}
                 </Box>
               </Tooltip>
               {/* report someone else's post to the moderators */}
               {currentUser && (
                 <ReportButton targetType="post" targetId={post.id}
                   targetLabel={`post by ${post.author_name || 'this user'}`} sx={{ ml: 0.25, color: 'text.secondary' }} />
               )}
             </>
           )}
 
       

        
        </Box>



       
       
        
 

        {/* Comments Section */}
        <Collapse in={showComments} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2 }}>
            <Comments 
              targetId={post.id}
              targetType="post"
              currentUser={currentUser}
              onCommentCountChange={handleCommentCountChange}
              maxDepth={1}  
            />
          </Box>
        </Collapse>
      </Box>

      {/* Dialogs */}
      <MeetingScheduleDialog 
        open={meetingDialogOpen} 
        onClose={() => setMeetingDialogOpen(false)} 
        restrictToUsers={ [ author ]}
        onDone={() => recordPostEvent(post, 'meet')}
      />

      {/* invite / suggest to the post's author (not to yourself) */}
      <InviteToConsortiumDialog     
        open={inviteToConsortiumDialogOpen}
        onClose={() => setInviteToConsortiumDialogOpen(false)}
        user={author}
        onDone={() => recordPostEvent(post, 'invite')}
      />

      <SuggestEntityDialog
        open={suggestDialogOpen}
        onClose={() => setSuggestDialogOpen(false)}
        user={author}
        onDone={() => recordPostEvent(post, 'suggest')}
      />

    </Box>
  );
}

export default PostItem;
